// 大事な操作（点呼の中身を見る・人の登録・CSV）の前の再確認（仕様書「大事な操作の前の再確認」）。
// 最後に 6 桁を入れてから 5 分を過ぎていたら、もう一度 6 桁を入れてもらう
import { createContext, useCallback, useContext, useRef, useState, type FormEvent, type ReactNode } from 'react';
import type { MultiFactorResolver } from 'firebase/auth';
import { authTime, finishStepUp, isEmulator, startStepUp } from '../lib/firebase';
import { CodeField } from '../pages/Login';
import { Dialog } from './ui';

const REAUTH_SEC = 5 * 60;
/** 少し余裕を見る（操作の途中で 5 分を過ぎないように） */
const MARGIN_SEC = 30;

type Ctx = { ensureRecent: (why: string) => Promise<boolean> };
const StepUpCtx = createContext<Ctx>({ ensureRecent: async () => true });
export const useStepUp = () => useContext(StepUpCtx);

export function StepUpProvider({ children }: { children: ReactNode }) {
  const [ask, setAsk] = useState<{ why: string; resolver: MultiFactorResolver | null } | null>(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const done = useRef<((ok: boolean) => void) | null>(null);
  // 開いている間に別の画面からも求められたら、同じ入力を待つ
  const pending = useRef<Promise<boolean> | null>(null);

  /** 5 分以内に 6 桁を入れていれば true。そうでなければ入れてもらい、入れたら true */
  const ensureRecent = useCallback(async (why: string) => {
    if (Date.now() / 1000 - (await authTime()) < REAUTH_SEC - MARGIN_SEC) return true;
    if (pending.current) return pending.current;
    let resolver: MultiFactorResolver | null = null;
    try { resolver = await startStepUp(); } catch { return false; }
    if (!resolver && !isEmulator) return true;
    setCode(''); setErr(''); setAsk({ why, resolver });
    pending.current = new Promise<boolean>((res) => { done.current = res; });
    return pending.current;
  }, []);

  const close = (ok: boolean) => { setAsk(null); done.current?.(ok); done.current = null; pending.current = null; };
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setErr('');
    try { await finishStepUp(ask!.resolver, code); close(true); } catch { setErr('数字が違います。認証アプリの今の 6 桁を入れてください'); setCode(''); }
  };
  return (
    <StepUpCtx.Provider value={{ ensureRecent }}>
      {children}
      {ask && (
        <Dialog title="もう一度 6 桁を入れてください" onClose={() => close(false)}>
          <p style={{ margin: 0 }}>{ask.why}のため、認証アプリの 6 桁で本人か確かめます。</p>
          <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
            <CodeField value={code} onChange={setCode} />
            {err && <p className="err" role="alert">{err}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn" onClick={() => close(false)}>やめる</button><button className="btn pri" disabled={!/^\d{6}$/.test(code)}>確認する</button></div>
          </form>
        </Dialog>
      )}
    </StepUpCtx.Provider>
  );
}
